import { useCallback, useReducer, useState } from 'react'
import type { Dispatch, JSX, PropsWithChildren } from 'react'

import { Flex, Paragraph, toast } from '@buffer-mono/popcorn'

import type { NormalizedChannel } from '~publish/components/ChannelSelector'
import { CommunityEmptyState } from '~publish/components/CommunityEmptyState'
import {
  PublicPreviewBar,
  usePreviewModeSearchParam,
} from '~publish/components/PublicPreviewBar'

import { CommentFeed } from '../Comments/CommentsList'
import { NoCommentsFound } from '../Comments/CommentsList/EmptyStates'
import { NoMentionsFound } from '../Mentions/MentionsEmptyState'
import { CommentsTabIndicator } from '../components/CommentsTabIndicator'
import { MentionsTabIndicatorView } from '../components/MentionsTabIndicator'
import { EXAMPLE_COMMENTS } from './exampleComments'
import type { ExampleComment } from './exampleComments'
import { EXAMPLE_MENTIONS } from './exampleMentions'
import type { ExampleMention } from './exampleMentions'
import { createExampleReplayState, exampleReplayReducer } from './exampleReplay'
import type { ExampleReplayAction, ExampleReplayState } from './exampleReplay'
import { PublicCommunityChannelFilter } from './PublicCommunityChannelFilter'
import { PublicExampleCommentCard } from './PublicExampleCommentCard'
import { PublicExampleMentionCard } from './PublicExampleMentionCard'
import { usePublicCommunityTab } from './usePublicCommunityTab'

const EXAMPLE_CHANNELS: NormalizedChannel[] = EXAMPLE_COMMENTS.map(
  (comment) => ({
    id: comment.service,
    name: comment.networkName,
    service: comment.service,
    avatar: null,
  }),
)

export function PublicCommunityPreview({
  children,
}: PropsWithChildren): JSX.Element {
  const [on, setOn] = usePreviewModeSearchParam()
  const [tab, setTab] = usePublicCommunityTab()
  const [replay, dispatch] = useReducer(
    exampleReplayReducer,
    EXAMPLE_COMMENTS,
    createExampleReplayState,
  )
  const [channelIds, setChannelIds] = useState<string[]>([])
  const inScope = (service: string): boolean =>
    channelIds.length === 0 || channelIds.includes(service)
  const comments = EXAMPLE_COMMENTS.filter((comment) =>
    inScope(comment.service),
  )
  const mentions = EXAMPLE_MENTIONS.filter((mention) =>
    inScope(mention.service),
  )
  const unrepliedCommentCount = comments.filter(
    (comment) => replay[comment.id]?.status === 'unreplied',
  ).length
  const clearChannels = (): void => setChannelIds([])

  return (
    <CommunityEmptyState
      tab={tab}
      onTabChange={setTab}
      comments={
        on ? (
          <PublicExampleComments
            comments={comments}
            replay={replay}
            dispatch={dispatch}
            onClearFilters={clearChannels}
          />
        ) : undefined
      }
      mentions={
        on ? (
          <PublicExampleMentions
            mentions={mentions}
            onClearFilters={clearChannels}
          />
        ) : undefined
      }
      commentsTabIndicator={
        on ? <CommentsTabIndicator count={unrepliedCommentCount} /> : undefined
      }
      mentionsTabIndicator={
        on ? (
          <MentionsTabIndicatorView count={mentions.length} mode="counter" />
        ) : undefined
      }
      tabActions={
        on ? (
          <PublicCommunityChannelFilter
            channels={EXAMPLE_CHANNELS}
            value={channelIds}
            onChange={setChannelIds}
          />
        ) : undefined
      }
      previewBar={
        <PublicPreviewBar
          on={on}
          onChange={setOn}
          offLabel="See example comments"
          onLabel="These are example comments"
          aboutLabel="How preview mode works"
          aboutTooltip="How preview mode works"
          about={
            <Flex direction="column" gap="xs">
              <Paragraph size="md">
                Preview mode shows example comments, written to show how
                Community works. Replies, likes and resolves stay on this page
                and reset when you reload it.
              </Paragraph>
              <Paragraph size="md">
                Connect a channel to reply to your own comments.
              </Paragraph>
            </Flex>
          }
        />
      }
    >
      {children}
    </CommunityEmptyState>
  )
}

function PublicExampleComments({
  comments,
  replay,
  dispatch,
  onClearFilters,
}: {
  comments: ExampleComment[]
  replay: ExampleReplayState
  dispatch: Dispatch<ExampleReplayAction>
  onClearFilters: () => void
}): JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const expandNext = useCallback(
    (fromId?: string, onlyRepliable = false): void => {
      setExpandedId((currentId) => {
        const index = comments.findIndex(
          (comment) => comment.id === (fromId ?? currentId),
        )
        const following = comments.slice(index + 1)
        const next = onlyRepliable
          ? following.find(
              (comment) =>
                comment.id !== fromId &&
                replay[comment.id]?.status === 'unreplied',
            )
          : following[0]
        return next?.id ?? null
      })
    },
    [comments, replay],
  )

  const expandPrevious = useCallback((): void => {
    setExpandedId((currentId) => {
      const index = comments.findIndex((comment) => comment.id === currentId)
      return index > 0 ? comments[index - 1].id : currentId
    })
  }, [comments])

  if (comments.length === 0) {
    return <NoCommentsFound hasActiveFilters onClearFilters={onClearFilters} />
  }

  return (
    <CommentFeed aria-label="Example comments">
      {comments.map((comment) => (
        <PublicExampleCommentCard
          key={comment.id}
          comment={comment}
          state={replay[comment.id]}
          expanded={expandedId === comment.id}
          onManualExpansion={setExpandedId}
          onExpandNext={expandNext}
          onExpandPrevious={expandPrevious}
          onReply={(text): void => {
            dispatch({ type: 'reply', commentId: comment.id, text })
            toast.success(
              'Example reply added. Nothing was sent, and it resets when you reload.',
            )
          }}
          onToggleLike={(): void =>
            dispatch({ type: 'toggleLike', commentId: comment.id })
          }
          onResolve={(): void => {
            dispatch({ type: 'resolve', commentId: comment.id })
            toast.success(
              'Example comment resolved. It resets when you reload.',
            )
          }}
          onReopen={(): void =>
            dispatch({ type: 'reopen', commentId: comment.id })
          }
        />
      ))}
    </CommentFeed>
  )
}

function PublicExampleMentions({
  mentions,
  onClearFilters,
}: {
  mentions: ExampleMention[]
  onClearFilters: () => void
}): JSX.Element {
  if (mentions.length === 0) {
    return <NoMentionsFound hasActiveFilters onClearFilters={onClearFilters} />
  }

  return (
    <CommentFeed aria-label="Example mentions">
      {mentions.map((mention) => (
        <PublicExampleMentionCard key={mention.id} mention={mention} />
      ))}
    </CommentFeed>
  )
}
